import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, Users as UsersIcon, Ban, CheckCircle } from 'lucide-react';

function safeDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch(e) { return '—'; }
}

export default function Users() {
  const api = useApi();
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [u, b] = await Promise.all([
      api.get('/users').catch(() => null),
      api.get('/blocklist').catch(() => null)
    ]);
    if (u && !u.error) setUsers(u.users || []);
    if (b && !b.error) setBlocked(b.blocked || []);
    setLoading(false);
  }

  async function searchUsers() {
    if (!search.trim()) return load();
    setLoading(true);
    const d = await api.get('/users/search?q=' + encodeURIComponent(search)).catch(() => null);
    if (d && !d.error) setUsers(d.users || []);
    setLoading(false);
  }

  async function blockUser(phone) {
    const reason = window.prompt('Block karne ki wajah (optional):');
    if (reason === null) return;
    const d = await api.post('/blocklist', { phone, reason: reason || '' });
    if (d?.success) { toast.success('Block ho gaya'); load(); }
    else toast.error(d?.error || 'Block nahi hua');
  }

  async function unblock(phone) {
    if (!window.confirm(phone + ' ko unblock karein?')) return;
    const d = await api.del('/blocklist/' + phone);
    if (d?.success) { toast.success('Unblock ho gaya'); load(); }
    else toast.error(d?.error || 'Unblock nahi hua');
  }

  const blockedPhones = new Set(blocked.map(b => b.phone));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="section-title">👥 Users</div>
          <div className="section-sub">Bot ke registered users aur block list</div>
        </div>
        <div style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)', color: 'var(--emerald-300)' }}>
          {users.length} Users
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {['users', 'blocked'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 500, marginBottom: -1,
            borderBottom: tab === t ? '2px solid var(--emerald-400)' : '2px solid transparent',
            color: tab === t ? 'var(--emerald-300)' : 'var(--text-muted)'
          }}>
            {t === 'users' ? `👤 Users (${users.length})` : `🚫 Blocked (${blocked.length})`}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <>
          <div className="page-actions" style={{ marginBottom: 16 }}>
            <div className="search-wrap" style={{ flex: 1, maxWidth: 360, position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                className="input"
                placeholder="Phone, naam, city..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchUsers()}
                style={{ paddingLeft: 30 }}
              />
            </div>
            <button className="btn btn-ghost" onClick={searchUsers}>Search</button>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>
            ) : users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                <UsersIcon size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div>Koi user nahi mila</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['#', 'Phone', 'Naam', 'City', 'Date', ''].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{u.phone}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13 }}>{u.name || '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          {u.city
                            ? <span style={{ background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)', color: 'var(--emerald-300)', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{u.city}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)' }}>{safeDate(u.created_at)}</td>
                        <td style={{ padding: '10px 14px' }}>
                          {can('users') && !blockedPhones.has(u.phone) && (
                            <button
                              onClick={() => blockUser(u.phone)}
                              style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <Ban size={11} /> Block
                            </button>
                          )}
                          {blockedPhones.has(u.phone) && (
                            <span style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>Blocked</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'blocked' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>
          ) : blocked.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🚫</div>
              <div>Koi blocked user nahi</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Phone', 'Wajah', 'Date', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {blocked.map(b => (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{b.phone}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{b.reason || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)' }}>{safeDate(b.created_at)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {can('users') && (
                          <button
                            onClick={() => unblock(b.phone)}
                            style={{ background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)', color: 'var(--emerald-300)', padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <CheckCircle size={11} /> Unblock
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
